import { useEffect, useRef, useState } from "react";
import { audio } from "../../lib/audio";
import { dd, crits as mkCrits, resumeTarget, MISSION_ORDER, type DdCrit } from "./state";
import { Btn, Bubble, Choice, Code, Consequence, FixTag, KeepInstead, Maya, MissFooter, MissionHead, MissionResult, Seg, Todo, WhatIf, Win, useGuide, usePrefetch, type DdOpt } from "./ui";
import type { StageProps } from "./types";
import { useSim } from "../../state/store";
import { DD_ENTRY, DD_ROUTES } from "../../scenario/the-1700-release.content";

/* ================================================================== briefing (S1) */
/** A learner who has not picked a route starts on Version B. */
const DEFAULT_ROUTE = "b";
/** How long the route confirmation waits before it starts on its own. */
const CONFIRM_SECONDS = 10;

export function Briefing({ cfg, finish, onGuideChange }: StageProps) {
  const c = cfg.content as {
    kicker: string; title: string; cards: { title: string; body: string }[]; cta: string; note: string;
    requirements: string[]; missions: { id: string; time: string; title: string }[];
  };
  const [reqOpen, setReqOpen] = useState(false);
  const [resume] = useState(() => resumeTarget());
  const [routeId, setRouteId] = useState(() => dd.get().route?.id ?? DEFAULT_ROUTE);
  const [routeOpen, setRouteOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [count, setCount] = useState(CONFIRM_SECONDS);
  const [deadline, setDeadline] = useState(0);
  const routeRef = useRef<HTMLDivElement>(null);
  useGuide(onGuideChange, cfg.guide, "brief");
  // A fresh learner starts on Version B unless they already chose a route.
  useEffect(() => {
    if (dd.get().route) return;
    const r = DD_ROUTES.find((x) => x.id === DEFAULT_ROUTE);
    if (r) dd.setRoute(r.id, r.missions);
  }, []);
  useEffect(() => {
    if (!routeOpen) return;
    const away = (e: MouseEvent) => {
      if (routeRef.current && !routeRef.current.contains(e.target as Node)) setRouteOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [routeOpen]);
  useEffect(() => {
    const sync = () => setRouteId(dd.get().route?.id ?? DEFAULT_ROUTE);
    window.addEventListener("dd-state", sync);
    return () => window.removeEventListener("dd-state", sync);
  }, []);
  const pick = (id: string) => {
    audio.click();
    setRouteId(id);
    setRouteOpen(false);
    if (id === "custom") {
      dd.setRoute("custom", dd.route());
      window.dispatchEvent(new CustomEvent("dd-open-map"));
      return;
    }
    const r = DD_ROUTES.find((x) => x.id === id);
    if (r) dd.setRoute(id, r.missions);
  };
  const doStart = () => {
    const def = DD_ROUTES.find((x) => x.id === DEFAULT_ROUTE) ?? DD_ROUTES[0];
    const keep = dd.get().route ?? { id: def.id, missions: def.missions };
    dd.reset();
    dd.setRoute(keep.id, keep.missions);
    audio.ui("pageOpen");
    finish("continue");
  };
  // Starting first confirms the chosen route and shows the other ways in,
  // then auto-continues after CONFIRM_SECONDS so it never blocks a learner who already knows.
  const start = () => { audio.ui("pageOpen"); setRouteOpen(false); setCount(CONFIRM_SECONDS); setDeadline(Date.now() + CONFIRM_SECONDS * 1000); setConfirmOpen(true); };
  const switchRoute = (id: string) => {
    if (id === "custom") { setConfirmOpen(false); pick("custom"); return; }
    pick(id);
    setCount(CONFIRM_SECONDS);
    setDeadline(Date.now() + CONFIRM_SECONDS * 1000);
  };
  // Wall-clock countdown: a busy 3D frame can delay timers, so derive the
  // remaining seconds from the deadline instead of counting ticks.
  useEffect(() => {
    if (!confirmOpen) return;
    let fired = false;
    const tick = () => {
      if (fired) return;
      const left = Math.ceil((deadline - Date.now()) / 1000);
      if (left <= 0) { fired = true; setConfirmOpen(false); doStart(); return; }
      setCount(left);
    };
    tick();
    const t = window.setInterval(tick, 250);
    return () => { fired = true; window.clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmOpen, deadline]);
  const resumeGo = () => {
    if (!resume) return;
    audio.ui("pageOpen");
    useSim.getState().goToBeat(DD_ENTRY[resume]);
  };
  const selRoute = DD_ROUTES.find((x) => x.id === routeId);
  const resumeLabel = resume === "replay" ? "See your 17:00 replay" : resume ? `Continue at Mission ${MISSION_ORDER.indexOf(resume) + 1}` : "";
  return (
    <Win app="AsterCloud Release Board" tag={"S1 \u00b7 Briefing and evidence"} clock={cfg.clock}>
      <div className="dd-bf">
        <div className="dd-bf-head">
          <span className="dd-kicker">{c.kicker}</span>
          <h1>{c.title}</h1>
          <p className="dd-bf-sub">Choose your route, then watch the briefing video.</p>
        </div>
        <div className="dd-bf-goals">
          {c.cards.map((k, i) => (
            <div key={k.title} className="dd-bf-goal">
              <span className="dd-bf-n">{String(i + 1).padStart(2, "0")}</span>
              <b>{k.title}</b>
              <p>{k.body}</p>
            </div>
          ))}
        </div>
        <button type="button" className="dd-link dd-bf-reqlink" onClick={() => setReqOpen(true)}>
          Open the six requirements <span aria-hidden="true">{"\u2192"}</span>
        </button>
        <div className="dd-bf-route" ref={routeRef} onKeyDown={(e) => { if (e.key === "Escape" && routeOpen) { e.stopPropagation(); setRouteOpen(false); } }}>
          <span className="dd-bf-rh">Your route</span>
          <button type="button" className={`dd-bf-sel${routeOpen ? " is-open" : ""}`} aria-expanded={routeOpen} aria-haspopup="true" onClick={() => setRouteOpen((o) => !o)}>
            <svg className="dd-bf-tick" viewBox="0 0 22 22" width="22" height="22" aria-hidden="true">
              <circle cx="11" cy="11" r="11" fill="currentColor" />
              <path d="M6.2 11.4l3.3 3.3 6.3-6.6" fill="none" stroke="#0b1118" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <b>{selRoute ? selRoute.label : "Custom"}</b>
            <span className="dd-bf-meta">{selRoute ? `${selRoute.minutes.replace("-", "\u2013")} \u00b7 ${selRoute.missions.length} missions` : "you pick the missions"}</span>
            <span className="dd-bf-change">Change</span>
            <svg className="dd-bf-chev" viewBox="0 0 12 8" width="12" height="8" aria-hidden="true">
              <path d="M1 1.5l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {routeOpen && (
            <div className="dd-bf-panel" role="radiogroup" aria-label="Your route">
              {DD_ROUTES.map((r) => (
                <button key={r.id} type="button" role="radio" aria-checked={routeId === r.id} className={`dd-bf-opt${routeId === r.id ? " is-on" : ""}`} onClick={() => pick(r.id)}>
                  <span className="dd-bf-dot" aria-hidden="true" />
                  <b>{r.label}</b>
                  <span className="dd-bf-meta">{r.minutes.replace("-", "\u2013")} {"\u00b7"} {r.missions.length} missions</span>
                  <p>{r.detail}</p>
                </button>
              ))}
              <button type="button" role="radio" aria-checked={routeId === "custom"} className={`dd-bf-opt${routeId === "custom" ? " is-on" : ""}`} onClick={() => pick("custom")}>
                <span className="dd-bf-dot" aria-hidden="true" />
                <b>Custom</b>
                <span className="dd-bf-meta">you pick the missions</span>
                <p>Open the course map and tick exactly the topics that match your work.</p>
              </button>
            </div>
          )}
          <small className="dd-bf-hint">{DD_ROUTES.length + 1} routes available {"\u00b7"} Change route or jump to any chapter using Course map.</small>
        </div>
      </div>
      <footer className="dd-foot">
        <small>{c.note}</small>
        <div className="dd-foot-btns">
          {resume ? (
            <>
              <Btn onClick={start}>Start over</Btn>
              <Btn primary pulse onClick={resumeGo}>{resumeLabel}</Btn>
            </>
          ) : (
            <Btn primary pulse onClick={start}>{c.cta}</Btn>
          )}
        </div>
      </footer>
      {reqOpen && (
        <div className="dd-drawer" role="dialog" aria-label="Feature requirements">
          <div className="dd-drawer-h">
            <b>Feature requirements {"\u00b7"} customer report export</b>
            <Btn small onClick={() => setReqOpen(false)}>Close</Btn>
          </div>
          <ol className="dd-reqs">
            {c.requirements.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ol>
          <small className="dd-muted">These are AsterCloud's fictional policy choices for this exercise. Your organization's rules may differ.</small>
        </div>
      )}
      {confirmOpen && (
        <div className="dd-bf-confirm" role="dialog" aria-label="Your route">
          <div className="dd-bf-cbox">
            <span className="dd-kicker">Before you start</span>
            <h2>You{"\u2019"}re taking {selRoute ? selRoute.label : "a custom route"}</h2>
            {selRoute && <p className="dd-bf-cmeta">{selRoute.minutes.replace("-", "\u2013")} {"\u00b7"} {selRoute.missions.length} missions {"\u2014"} {selRoute.detail}</p>}
            <b className="dd-bf-cother">Other ways to take this course</b>
            <div className="dd-bf-calt">
              {DD_ROUTES.filter((r) => r.id !== routeId).map((r) => (
                <button key={r.id} type="button" className="dd-bf-calt-opt" onClick={() => switchRoute(r.id)}>
                  <b>{r.label}</b>
                  <span>{r.minutes.replace("-", "\u2013")} {"\u00b7"} {r.missions.length} missions</span>
                </button>
              ))}
              {routeId !== "custom" && (
                <button type="button" className="dd-bf-calt-opt" onClick={() => switchRoute("custom")}>
                  <b>Custom</b>
                  <span>pick the missions yourself</span>
                </button>
              )}
            </div>
            <p className="dd-bf-ctip"><b>Tip:</b> open <b>Course map</b> (top left) at any time to jump straight to any lesson.</p>
            <div className="dd-bf-cfoot">
              <span className="dd-bf-count">Starting in {count}{"\u2026"}</span>
              <Btn primary pulse onClick={() => { setConfirmOpen(false); doStart(); }}>OK, start</Btn>
            </div>
          </div>
        </div>
      )}
    </Win>
  );
}

/* ================================================================== film (S2) */
export function Film({ cfg, finish, onGuideChange, rec }: StageProps) {
  const f = cfg.content as { title: string; intro: string; src: string; poster: string; outro: string };
  const [stage, setStage] = useState<"idle" | "playing" | "done">("idle");
  const videoRef = useRef<HTMLVideoElement>(null);
  useGuide(onGuideChange, cfg.guide, stage);
  usePrefetch([f.outro]);
  useEffect(() => () => {
    if (videoRef.current) audio.detachFilm(videoRef.current);
  }, []);
  const end = (watched: boolean) => {
    const v = videoRef.current;
    const seen = watched || (!!v && v.duration > 0 && v.currentTime / v.duration > 0.9);
    if (v) {
      v.pause();
      audio.detachFilm(v);
    }
    dd.set({ video: seen ? "watched" : "skipped" });
    if (seen) rec("dd_video_watched", "watched");
    setStage("done");
  };
  return (
    <Win app="AsterCloud Learning" tag={"S2 \u00b7 Video"} clock={cfg.clock}>
      {stage === "playing" ? (
        <div className="dd-film is-playing">
          <video
            ref={videoRef}
            src={f.src}
            poster={f.poster}
            controls
            autoPlay
            playsInline
            onPlay={() => videoRef.current && audio.attachFilm(videoRef.current, 6)}
            onEnded={() => end(true)}
          />
          <div className="dd-film-skip">
            <Btn small onClick={() => end(false)}>Skip the rest</Btn>
          </div>
        </div>
      ) : (
        <div className="dd-film">
          <div className="dd-film-poster" style={{ backgroundImage: `url(${f.poster})` }} />
          <div className="dd-film-copy">
            <span className="dd-kicker">Briefing video</span>
            <h2>{f.title}</h2>
            {stage === "idle" ? (
              <>
                <p>{f.intro}</p>
                <div className="dd-row">
                  <Btn primary pulse onClick={() => setStage("playing")}>Watch the video</Btn>
                  <Btn onClick={() => end(false)}>Skip the video</Btn>
                </div>
              </>
            ) : (
              <>
                <Maya p={cfg.people.maya} text={f.outro} compact />
                <div className="dd-row">
                  <Btn primary pulse onClick={() => finish("continue")}>Open the feature request</Btn>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Win>
  );
}

/* ================================================================== D1a work chat (S7, portrait) */
interface D1Content {
  jordan: string; mayaChat: string; maya: string; task: string;
  workspaces: DdOpt[]; workspaceFeedback: Record<string, string>;
  cards: { id: string; label: string; detail: string; body: string; redacted?: string; options: string[]; scan: string[]; scanRedacted?: string[]; flags?: string[] }[];
  prompt: string; sensitive: Record<string, string>; sensitiveWhy: Record<string, string>;
  responseActions: { id: string; label: string; need: "always" | "env" | "data" | "never"; why: string; notNeeded?: string }[];
  okAssistant: string; restrictiveAssistant: string;
  crits: { id: string; label: string }[];
  requirements?: string[];
}

export function D1Chat({ cfg, finish, onGuideChange }: StageProps) {
  const c = cfg.content as D1Content;
  const [pick, setPick] = useState<string>();
  const [committed, setCommitted] = useState<string>();
  const [reqOpen, setReqOpen] = useState(false);
  const ok = committed === "managed";
  useGuide(onGuideChange, cfg.guide, ok ? "chat_ok" : "chat");
  const commit = () => {
    if (!pick) return;
    if (!dd.get().workspace) dd.set({ workspace: pick });
    setCommitted(pick);
    audio.ui(pick === "managed" ? "success" : "error");
  };
  return (
    <Win app="Work chat" tag="S7" clock={cfg.clock}>
      <div className="dd-chat">
        <div className="dd-chan"># orbitdesk-4-8 {"\u00b7"} Release day</div>
        <Bubble p={cfg.people.jordan}>{c.jordan}</Bubble>
        <Bubble p={cfg.people.maya}>
          {c.mayaChat}
          <button type="button" className="dd-attach" onClick={() => { audio.ui("docOpen"); setReqOpen(true); }}>
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: "-2px", marginRight: "4px" }}><path d="m20 11-8.5 8.5a5 5 0 0 1-7-7L13 4a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L14 7" /></svg>
            Open the requirements (6)
          </button>
        </Bubble>
        <div className="dd-task">
          <span className="dd-kicker">Mission 1 of 10 {"\u00b7"} 09:00</span>
          <b>Task: {c.task}</b>
          <p>Which AI workspace will you use for this task?</p>
          <Choice options={c.workspaces} value={pick} onChange={(v) => { setPick(v); setCommitted(undefined); }} disabled={ok} />
          {committed && (
            <Consequence tone={ok ? "ok" : "warn"} title={ok ? "Approved workspace" : "Not this one"}>
              {c.workspaceFeedback[committed]}
            </Consequence>
          )}
          <div className="dd-row">
            {ok ? (
              <Btn primary pulse onClick={() => finish("continue")}>Open the context builder</Btn>
            ) : (
              <>
                {committed && <Btn onClick={() => { setPick("managed"); setCommitted("managed"); audio.ui("success"); }}>Switch to the approved workspace</Btn>}
                <Btn primary pulse={!!pick} disabled={!pick || committed === pick} onClick={commit}>Use this workspace</Btn>
              </>
            )}
          </div>
        </div>
      </div>
      {reqOpen && (
        <div className="dd-drawer is-full" role="dialog" aria-label="Feature requirements">
          <div className="dd-drawer-h">
            <b>Feature requirements</b>
            <Btn small onClick={() => setReqOpen(false)}>Close</Btn>
          </div>
          <ol className="dd-reqs">
            {(c.requirements ?? []).map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ol>
          <small className="dd-muted">Fictional AsterCloud policy for this exercise.</small>
        </div>
      )}
    </Win>
  );
}

/* ================================================================== D1b context builder (S3) */
const TREAT_LABEL: Record<string, string> = { include: "Include", redact: "Redact", exclude: "Exclude" };
const D1_GOOD: Record<string, string[]> = {
  handler: ["include"], requirements: ["include"], fixture: ["include"], errlog: ["include"],
  debuglog: ["redact", "exclude"], env: ["exclude"], prodexport: ["exclude"],
};

/** "Sending" + "scanning" visualization: every included card travels to the
 *  assistant one by one, and the lines it extracts appear under it; findings
 *  that should never have been sent are flagged. */
function SendViz({ items, onDone }: { items: { id: string; label: string; redacted: boolean; lines: string[]; flags: string[] }[]; onDone: () => void }) {
  const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const steps = items.reduce((s, it) => s + 1 + it.lines.length + it.flags.length, 0);
  const [tick, setTick] = useState(reduce ? steps : 0);
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const el = listRef.current?.querySelector(".is-busy") as HTMLElement | null;
    if (el && listRef.current) listRef.current.scrollTop = el.offsetTop - listRef.current.offsetTop - 8;
  }, [tick]);
  useEffect(() => {
    if (tick >= steps) return;
    const t = window.setTimeout(() => {
      setTick((v) => v + 1);
      audio.ui("key", 0.35);
    }, 420);
    return () => window.clearTimeout(t);
  }, [tick, steps]);
  let budget = tick;
  const flagged = items.reduce((s, it) => s + it.flags.length, 0);
  const done = tick >= steps;
  return (
    <div className="dd-send">
      <div className="dd-send-h">
        <b>{done ? "Bundle received by the coding assistant" : "Sending to the coding assistant\u2026"}</b>
        <span className="dd-muted">AsterCloud managed workspace</span>
      </div>
      <div className="dd-send-bar"><span style={{ width: `${Math.round((Math.min(tick, steps) / Math.max(1, steps)) * 100)}%` }} /></div>
      <ul ref={listRef} className="dd-send-list">
        {items.map((it) => {
          if (budget <= 0) return <li key={it.id} className="is-queued"><span className="dd-send-ic" aria-hidden="true">{"\u2022"}</span>{it.label}<small> queued</small></li>;
          budget -= 1;
          const shownLines = it.lines.slice(0, Math.max(0, budget));
          budget -= shownLines.length;
          const shownFlags = it.flags.slice(0, Math.max(0, budget));
          budget -= shownFlags.length;
          const complete = shownLines.length === it.lines.length && shownFlags.length === it.flags.length;
          return (
            <li key={it.id} className={complete ? "is-done" : "is-busy"}>
              <span className="dd-send-ic" aria-hidden="true">{complete ? "\u2713" : "\u21BB"}</span>
              <b>{it.label}</b>{it.redacted && <small> (redacted view)</small>}
              {shownLines.map((l) => <div key={l} className="dd-scan">{"\u203A"} {l}</div>)}
              {shownFlags.map((l) => <div key={l} className="dd-scan is-flag">{"\u26A0"} Found: {l}</div>)}
            </li>
          );
        })}
      </ul>
      <div className="dd-actions">
        {!done && <Btn small onClick={() => setTick(steps)}>Show everything now</Btn>}
        {done && <span className={flagged ? "dd-warnline" : "dd-muted"}>{items.length} items received {"\u00b7"} {flagged ? `${flagged} restricted findings` : "no restricted findings"}</span>}
        <Btn primary pulse={done} disabled={!done} onClick={onDone}>See what happened</Btn>
      </div>
    </div>
  );
}

/** Inline line icon for a context-bundle card (code, document, data, settings, table). */
function D1Icon({ id }: { id: string }) {
  const p = { viewBox: "0 0 24 24", width: 20, height: 20, fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
  if (id === "handler") return <svg {...p}><rect x="4" y="3" width="16" height="18" rx="2.5" /><path d="m10.5 9.5-2.5 2.5 2.5 2.5M13.5 9.5l2.5 2.5-2.5 2.5" /></svg>;
  if (id === "fixture") return <svg {...p}><ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" /></svg>;
  if (id === "env") return <svg {...p}><circle cx="12" cy="12" r="3" /><path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1" /></svg>;
  if (id === "prodexport") return <svg {...p}><rect x="3.5" y="4" width="17" height="16" rx="2.5" /><path d="M3.5 10h17M3.5 15h17M10 4v16" /></svg>;
  return <svg {...p}><path d="M14 3H7.5A2.5 2.5 0 0 0 5 5.5v13A2.5 2.5 0 0 0 7.5 21h9a2.5 2.5 0 0 0 2.5-2.5V8Z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></svg>;
}

export function D1Context({ cfg, finish, onGuideChange, rec }: StageProps) {
  const c = cfg.content as D1Content;
  const [treat, setTreat] = useState<Record<string, string>>({});
  const [view, setView] = useState<string | null>(null);
  const [stage, setStage] = useState<"build" | "review" | "sending" | "respond" | "result" | "fix" | "fixed">("build");
  const [first, setFirst] = useState<DdCrit[] | null>(null);
  const [sent, setSent] = useState<{ sensitive: string[]; restrictive: boolean } | null>(null);
  const [resp, setResp] = useState<Record<string, boolean>>({});
  const [respChecked, setRespChecked] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [explained, setExplained] = useState(false);
  const [fixMsg, setFixMsg] = useState("");
  useGuide(onGuideChange, cfg.guide, stage === "fixed" ? "result" : stage);
  usePrefetch([c.maya]);
  const all = c.cards.every((k) => treat[k.id]);
  const isGood = (id: string) => D1_GOOD[id].includes(treat[id]);
  const [selected, setSelected] = useState<string>(() => c.cards[0]?.id ?? "");
  /** Respond stage inspector: a response action id, or "f:<cardId>" for a leaked-card finding. */
  const [respSel, setRespSel] = useState<string>("");
  useEffect(() => {
    if (stage !== "fix") return;
    const wrong = c.cards.find((k) => !D1_GOOD[k.id].includes(treat[k.id]));
    if (wrong) setSelected(wrong.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const send = () => {
    const sensitive = c.cards.filter((k) => c.sensitive[k.id] && treat[k.id] === "include").map((k) => k.id);
    const restrictive = treat.handler === "exclude" || treat.requirements === "exclude";
    const ws = dd.get().workspace;
    const lbl = (id: string) => `${c.cards.find((k) => k.id === id)?.label}: ${TREAT_LABEL[treat[id]] ?? "-"}`;
    const wsLabel = c.workspaces.find((w) => w.id === ws)?.label ?? "-";
    const cr = mkCrits(
      c.crits,
      {
        workspace: ws === "managed",
        code: treat.handler === "include" && treat.requirements === "include",
        fixture: treat.fixture === "include",
        diag: treat.errlog === "include" && treat.debuglog !== "include",
        secrets: treat.env === "exclude" && treat.prodexport === "exclude",
      },
      {
        workspace: `first choice: ${wsLabel}`,
        code: `${lbl("handler")}; ${lbl("requirements")}`,
        fixture: lbl("fixture"),
        diag: `${lbl("errlog")}; ${lbl("debuglog")}`,
        secrets: `${lbl("env")}; ${lbl("prodexport")}`,
      },
    );
    const kept = dd.commitFirst("d1", cr, `${c.cards.filter((k) => treat[k.id] !== "exclude").length} of 7 cards sent${sensitive.length ? ", including restricted data" : ""}`);
    setFirst(kept);
    rec("dd_d1_first", `${dd.missionScore("d1")}/10`);
    setSent({ sensitive, restrictive });
    if (sensitive.length) {
      rec("dd_d1_disclosure", sensitive.map((s) => c.sensitive[s]).join("; "));
      dd.log("d1", `Restricted data entered the assistant's context: ${sensitive.map((s) => c.sensitive[s]).join("; ")}.`, "consequence");
    }
    audio.ui("message");
    setStage("sending");
  };
  const afterSend = () => {
    if (sent?.sensitive.length) {
      audio.ui("error");
      setStage("respond");
    } else {
      audio.ui(sent?.restrictive ? "error" : "success");
      setStage("result");
    }
  };

  const hasEnv = !!sent?.sensitive.includes("env");
  const hasData = !!sent?.sensitive.some((s) => s !== "env");
  const isNeeded = (a: D1Content["responseActions"][number]) => a.need === "always" || (a.need === "env" && hasEnv) || (a.need === "data" && hasData);
  const missing = c.responseActions.filter((a) => isNeeded(a) && !resp[a.id]);
  const wrongPicked = c.responseActions.filter((a) => a.need === "never" && resp[a.id]);
  const respOk = missing.length === 0 && wrongPicked.length === 0;
  const anyResp = Object.values(resp).some(Boolean);
  const verdict = (a: D1Content["responseActions"][number]) => {
    const on = !!resp[a.id];
    if (a.need === "never") return on ? { cls: "is-bad", t: "Does not help" } : { cls: "is-muted", t: "Correctly left out" };
    if (isNeeded(a)) return on ? { cls: "is-good", t: "Needed now" } : { cls: "is-miss", t: "Missing: needed now" };
    return on ? { cls: "is-muted", t: "Not needed here" } : { cls: "is-muted", t: "Not needed here" };
  };

  const resend = () => {
    if (!c.cards.every((k) => isGood(k.id))) {
      setFixMsg("Some cards still need a fix.");
      audio.ui("error");
      return;
    }
    dd.log("d1", "Rebuilt the context bundle safely.", "corrected");
    audio.ui("success");
    setStage("fixed");
  };

  const allOk = !!first && first.every((x) => x.ok);
  const mayFix = !allOk && c.cards.some((k) => !isGood(k.id));

  /** Header shared by every stage of the exercise (build, fix, review, sending, respond). */
  const wsOn = dd.get().workspace === "managed" || !dd.get().workspace ? "AsterCloud managed" : "AsterCloud managed (switched)";
  const stageHead = (
    <header className="dd1c-head">
      <div className="dd1c-head-l">
        <span className="dd1c-kicker">Mission 1 of 10</span>
        <h2>Give it the whole project?</h2>
        <p className="dd1c-task">Task: {c.task}</p>
        <p className="dd1c-info">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 7.8v.2" /></svg>
          Opening a preview is not sending it. Nothing leaves your machine until you click Send.
        </p>
      </div>
      <span className="dd1c-chip">Workspace: {wsOn}</span>
    </header>
  );

  /** The build / fix presentation: header, two columns (list + inspector) and the bottom bar. */
  const rows = (fix: boolean) => {
    const total = c.cards.length;
    const idx = Math.max(0, c.cards.findIndex((k) => k.id === selected));
    const sel = c.cards[idx];
    const decided = c.cards.filter((k) => treat[k.id]).length;
    const wrongCount = fix ? c.cards.filter((k) => !isGood(k.id)).length : 0;
    const go = (to: number) => {
      const next = c.cards[(to + total) % total];
      if (next) { audio.click(); setSelected(next.id); }
    };
    return (
      <div className="dd1c">
        {stageHead}
        <div className="dd1c-cols">
          <section className="dd1c-left" aria-label="Context bundle">
            <div className="dd1c-lh">
              <b>{fix ? "Rebuild the bundle" : "Context bundle"}</b>
              <span>{total} items</span>
            </div>
            <p className="dd1c-sub">{fix ? "Fix the flagged items, then send again." : "Select an item to inspect and decide."}</p>
            <ul className="dd1c-list">
              {c.cards.map((k) => {
                const wrong = fix && !isGood(k.id);
                const set = treat[k.id];
                return (
                  <li key={k.id}>
                    <button
                      type="button"
                      className={`dd1c-row${k.id === sel?.id ? " is-sel" : ""}${wrong ? " is-fix" : ""}${set ? " is-set" : ""}`}
                      aria-current={k.id === sel?.id ? "true" : undefined}
                      onClick={() => { audio.click(); setSelected(k.id); }}
                    >
                      <span className="dd1c-ic"><D1Icon id={k.id} /></span>
                      <span className="dd1c-rt">
                        <b>{k.label}{wrong && <FixTag />}</b>
                        <small title={k.detail}>{k.detail}</small>
                      </span>
                      <span className={`dd1c-st${set ? ` is-${set}` : ""}${wrong ? " is-fix" : ""}`}>
                        {set && (
                          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
                        )}
                        {set ? TREAT_LABEL[set] : "Pending"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
          {sel && (
            <section className="dd1c-right" aria-label={sel.label}>
              <div className="dd1c-rh">
                <span>Item {idx + 1} of {total}</span>
                <span className="dd1c-nav">
                  <button type="button" aria-label="Previous item" onClick={() => go(idx - 1)}>
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 5-7 7 7 7" /></svg>
                  </button>
                  <button type="button" aria-label="Next item" onClick={() => go(idx + 1)}>
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
                  </button>
                </span>
              </div>
              <h3>{sel.label}{fix && !isGood(sel.id) && <FixTag />}</h3>
              <p className="dd1c-meta">{sel.detail}</p>
              <b className="dd1c-sec">What this contains</b>
              <ul className="dd1c-contains">
                {sel.scan.map((s) => <li key={s}>{s}</li>)}
              </ul>
              <button type="button" className="dd1c-open" onClick={() => { audio.click(); setView(sel.id); }}>
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></svg>
                Open preview
              </button>
              <small className="dd1c-note">Inspect the contents before making your choice.</small>
              <div className="dd1c-rule" />
              <b className="dd1c-sec">What should the assistant receive?</b>
              <div className="dd1c-act">
                <Seg options={sel.options.map((o) => ({ id: o, label: TREAT_LABEL[o] }))} value={treat[sel.id]} onChange={(v) => setTreat((t) => ({ ...t, [sel.id]: v }))} />
              </div>
              <small className="dd1c-note">Choose one action. You can change it before sending.</small>
            </section>
          )}
        </div>
        <footer className="dd1c-foot">
          <span className={`dd1c-count${(fix ? wrongCount === 0 : all) ? " is-all" : ""}`}>
            <i aria-hidden="true">{(fix ? wrongCount === 0 : all) ? "\u2713" : ""}</i>
            <b>{decided}</b> of {total} decided
          </span>
          {fix ? (
            <span className={wrongCount ? "dd-warnline" : "dd-muted"}>
              {fixMsg || (wrongCount ? `${wrongCount} flagged item${wrongCount > 1 ? "s" : ""} still need${wrongCount > 1 ? "" : "s"} a fix` : "Every item is set")}
            </span>
          ) : (
            <span className="dd-muted">{all ? "Ready to review" : `Decide on all ${total} items to review`}</span>
          )}
          <span className="dd1c-foot-r">
            {fix ? (
              <>
                <KeepInstead onClick={() => { setExplained(true); setFixMsg(""); setStage("result"); }} />
                <Btn primary pulse onClick={resend}>Send again</Btn>
              </>
            ) : (
              <Btn primary pulse={all} disabled={!all} onClick={() => setStage("review")}>Review bundle {"\u2192"}</Btn>
            )}
          </span>
        </footer>
      </div>
    );
  };

  const viewCard = c.cards.find((k) => k.id === view);
  const viewDrawer = viewCard && (
    <div className="dd-drawer" role="dialog" aria-label={viewCard.label}>
      <div className="dd-drawer-h">
        <b>{viewCard.label}</b>
        <Btn small onClick={() => setView(null)}>Close</Btn>
      </div>
      <small className="dd-muted">{viewCard.detail}</small>
      <Code small>{viewCard.body}</Code>
      {viewCard.redacted && (
        <>
          <b className="dd-sub">Redaction view (what Redact would send)</b>
          <Code small>{viewCard.redacted}</Code>
        </>
      )}
    </div>
  );

  if ((stage === "result" || stage === "fixed") && first) {
    const lblOf = (id: string) => c.cards.find((k) => k.id === id)?.label ?? id;
    return (
      <Win app={"OrbitIDE \u00b7 Coding assistant"} tag={"S3 \u00b7 IDE"} clock={cfg.clock}>
        <MissionResult
          n={1}
          time="09:05"
          title="Give it the whole project?"
          task={c.task}
          contextChip={`Workspace: ${dd.get().workspace === "managed" || !dd.get().workspace ? "AsterCloud managed" : "AsterCloud managed (switched)"}`}
          onTaskDetails={() => setTaskOpen(true)}
          crits={first}
          expected={{
            workspace: { "first choice": c.workspaces.find((w) => w.id === "managed")?.label ?? "AsterCloud managed" },
            code: { [lblOf("handler")]: "Include", [lblOf("requirements")]: "Include" },
            fixture: { [lblOf("fixture")]: "Include" },
            diag: { [lblOf("errlog")]: "Include", [lblOf("debuglog")]: "Redact or Exclude" },
            secrets: { [lblOf("env")]: "Exclude", [lblOf("prodexport")]: "Exclude" },
          }}
          outcomes={
            stage === "fixed"
              ? [{ tone: "ok", title: "Useful context received \u00b7 No production records or credentials included", body: "Corrected during training. The original attempt stays in your replay." }]
              : sent?.restrictive
                ? undefined
                : sent?.sensitive.length
                  ? [{ tone: "warn", title: "Response recorded", body: "The disclosure stays in your history even after you rebuild the bundle." }]
                  : [{ tone: "ok", title: "Useful context received \u00b7 No production records or credentials included" }]
          }
          assistant={
            stage !== "fixed" && (sent?.restrictive || !sent?.sensitive.length)
              ? { person: cfg.people.assistant, text: sent?.restrictive ? c.restrictiveAssistant : c.okAssistant, tone: sent?.restrictive ? "warn" : "ai" }
              : undefined
          }
          maya={{ person: cfg.people.maya, text: c.maya, speak: stage === "result" }}
          footer={stage === "fixed" ? (
              <Btn primary pulse onClick={() => { dd.master("d1", true); finish("continue"); }}>Continue</Btn>
            ) : (
              <MissFooter
                allOk={allOk}
                explained={explained || !mayFix}
                onExplain={() => { setExplained(true); audio.ui("docOpen"); }}
                onRetry={() => { setFixMsg(""); setStage("fix"); }}
                onContinue={() => { if (allOk) dd.master("d1", false); else dd.acknowledge("d1"); finish("continue"); }}
              />
            )}
        />
        {taskOpen && (
          <div className="dd-drawer" role="dialog" aria-label="Task details">
            <div className="dd-drawer-h">
              <b>Task details</b>
              <Btn small onClick={() => setTaskOpen(false)}>Close</Btn>
            </div>
            <small className="dd-muted">{c.task}</small>
            <b className="dd-sub">Opening a card is not sending it.</b>
            <p>Nothing leaves your machine until you click Send.</p>
          </div>
        )}
      </Win>
    );
  }
  if (stage === "build" || stage === "fix") {
    return (
      <Win app={"OrbitIDE \u00b7 Coding assistant"} tag={"S3 \u00b7 IDE"} clock={cfg.clock}>
        {rows(stage === "fix")}
        {viewDrawer}
      </Win>
    );
  }
  return (
    <Win app={"OrbitIDE \u00b7 Coding assistant"} tag={"S3 \u00b7 IDE"} clock={cfg.clock}>
      <div className="dd1c">
        {stageHead}
          {stage === "review" && (
            <>
              <section className="dd1c-panel" aria-label="Bundle preview">
                <div className="dd1c-lh">
                  <b>Bundle preview</b>
                  <span>Exactly what the assistant will receive</span>
                </div>
                <ul className="dd1c-bundle">
                  {c.cards.filter((k) => treat[k.id] !== "exclude").map((k) => (
                    <li key={k.id}>
                      <span className="dd1c-ic"><D1Icon id={k.id} /></span>
                      <span>{k.label}{treat[k.id] === "redact" ? " (redacted view)" : ""}</span>
                    </li>
                  ))}
                  {c.cards.every((k) => treat[k.id] === "exclude") && <li className="is-empty">Nothing selected</li>}
                </ul>
                <b className="dd1c-sec">Task prompt (assembled from the requirement cards)</b>
                <Code small>{c.prompt}</Code>
              </section>
              <footer className="dd1c-foot">
                <span className="dd1c-foot-l"><Btn onClick={() => setStage("build")}>Back</Btn></span>
                <span className="dd1c-foot-r is-end"><Btn primary pulse onClick={send}>Send to coding assistant</Btn></span>
              </footer>
            </>
          )}
          {stage === "sending" && (
            <div className="dd1c-send">
            <SendViz
              items={c.cards.filter((k) => treat[k.id] !== "exclude").map((k) => ({
                id: k.id,
                label: k.label,
                redacted: treat[k.id] === "redact",
                lines: treat[k.id] === "redact" ? k.scanRedacted ?? k.scan : k.scan,
                flags: treat[k.id] === "include" ? k.flags ?? [] : [],
              }))}
              onDone={afterSend}
            />
            </div>
          )}
          {stage === "respond" && sent && (() => {
            const acts = c.responseActions;
            const findCard = respSel.startsWith("f:") ? c.cards.find((k) => k.id === respSel.slice(2)) : undefined;
            const act = findCard ? undefined : acts.find((a) => a.id === respSel) ?? acts[0];
            const actIdx = act ? acts.indexOf(act) : -1;
            const stateIcon = (cls: string) => (
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {cls === "is-good" ? <path d="m5 12.5 4.5 4.5L19 7.5" /> : cls === "is-miss" ? <path d="M12 5v8M12 18v.2" /> : cls === "is-bad" ? <path d="m6 6 12 12M18 6 6 18" /> : <path d="M6 12h12" />}
              </svg>
            );
            return (
              <>
                <div className="dd1c-risk">
                  <Consequence tone="risk" title="Restricted data entered the model context">
                    This is a disclosure into an approved tool, not a public leak. It still needs a response now, not at the end of the day.
                  </Consequence>
                </div>
                <div className="dd1c-cols">
                  <section className="dd1c-left" aria-label="Respond now">
                    <div className="dd1c-lh">
                      <b>Respond now</b>
                      <span>{acts.length} possible actions</span>
                    </div>
                    <p className="dd1c-sub">Select what you would do, then confirm. You get feedback on every action.</p>
                    <ul className="dd1c-list">
                      {acts.map((a) => {
                        const v = verdict(a);
                        const on = !!resp[a.id];
                        return (
                          <li key={a.id} className={`dd1c-arow${act?.id === a.id ? " is-sel" : ""}${on ? " is-on" : ""}${respChecked ? ` ${v.cls}` : ""}`}>
                            <button
                              type="button"
                              role="checkbox"
                              aria-checked={on}
                              aria-label={a.label}
                              className="dd1c-tog"
                              onClick={() => {
                                audio.click();
                                setRespChecked(false);
                                setResp((r) => ({ ...r, [a.id]: !r[a.id] }));
                                setRespSel(a.id);
                              }}
                            >
                              <span className="dd1c-cb" aria-hidden="true">
                                {on && <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth={3.4} strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>}
                              </span>
                            </button>
                            <button type="button" className="dd1c-abody" aria-current={act?.id === a.id ? "true" : undefined} onClick={() => { audio.click(); setRespSel(a.id); }}>
                              <span className="dd1c-alabel">{a.label}</span>
                              {respChecked && <span className={`dd1c-vd ${v.cls}`}>{stateIcon(v.cls)}{v.t}</span>}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                  <section className="dd1c-right" aria-label={findCard ? "What was critical" : "Action detail"}>
                    <div className="dd1c-rh">
                      <span>{findCard ? "What was critical" : `Action ${actIdx + 1} of ${acts.length}`}</span>
                    </div>
                    <div className="dd1c-detail">
                      {findCard ? (
                        <>
                          <h3>{findCard.label}</h3>
                          <ul className="dd1c-flags">{(findCard.flags ?? []).map((f) => <li key={f}>{f}</li>)}</ul>
                          <p className="dd1c-why"><b>Why it matters:</b> {c.sensitiveWhy[findCard.id]}</p>
                        </>
                      ) : act && (
                        <>
                          <h3>{act.label}</h3>
                          <p className="dd1c-pick">{resp[act.id] ? "Selected for your response" : "Not selected"}</p>
                          {respChecked ? (
                            <div className={`dd1c-verdict ${verdict(act).cls}`}>
                              <b>{verdict(act).t}.</b> {isNeeded(act) || act.need === "never" ? act.why : act.notNeeded ?? act.why}
                            </div>
                          ) : (
                            <p className="dd1c-meta">Tick the actions you would take, then confirm your response to see whether each one is needed and why.</p>
                          )}
                        </>
                      )}
                    </div>
                    <div className="dd1c-crit">
                      <b className="dd1c-sec">What was critical</b>
                      {sent.sensitive.map((s) => {
                        const card = c.cards.find((k) => k.id === s);
                        return card ? (
                          <button key={s} type="button" className={`dd1c-crit-b${respSel === `f:${s}` ? " is-sel" : ""}`} onClick={() => { audio.click(); setRespSel(`f:${s}`); }}>
                            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3 2.5 20h19Z" /><path d="M12 10v5M12 17.6v.2" /></svg>
                            {card.label}
                          </button>
                        ) : null;
                      })}
                    </div>
                  </section>
                </div>
                <footer className="dd1c-foot">
                  <span className="dd1c-foot-msg">
                    {respChecked && !respOk && <span className="dd-warnline">Not complete yet: {missing.length ? `add ${missing.length} missing action${missing.length > 1 ? "s" : ""}` : ""}{missing.length && wrongPicked.length ? " and " : ""}{wrongPicked.length ? "remove the action that does not help" : ""}, then confirm again.</span>}
                  </span>
                  <span className="dd1c-foot-r">
                    {respChecked && !respOk && (
                      <Btn onClick={() => {
                        const r: Record<string, boolean> = {};
                        c.responseActions.forEach((a) => { if (isNeeded(a)) r[a.id] = true; });
                        setResp(r);
                        setRespChecked(true);
                        audio.ui("success");
                      }}>Use the recommended response</Btn>
                    )}
                    {respChecked && respOk ? (
                      <Btn primary pulse onClick={() => { dd.log("d1", "Stopped sharing, reported the exposure and involved the owners.", "note"); setStage("result"); }}>Continue</Btn>
                    ) : (
                      <Btn primary pulse={anyResp} disabled={!anyResp} onClick={() => { setRespChecked(true); audio.ui(respOk ? "success" : "error"); }}>Confirm response</Btn>
                    )}
                  </span>
                </footer>
              </>
            );
          })()}
      </div>
      {viewDrawer}
    </Win>
  );
}

/* ================================================================== D2 agent settings (S3, landscape) */
interface CapOpt { id: string; label: string; rule: string; example: string; result: string }
interface Cap { id: string; label: string; question?: string; options: CapOpt[]; good: string }
interface D2Content { assistant: string; explainer: string; caps: Cap[]; enforced: string; script: string; maya: string; crits: { id: string; label: string }[] }

export function D2({ cfg, finish, onGuideChange, rec }: StageProps) {
  const c = cfg.content as D2Content;
  const [prof, setProf] = useState<Record<string, string>>(() => ({ ...(dd.get().scope ?? {}) }));
  const [focus, setFocus] = useState<{ cap: string; opt: string } | null>(null);
  const [stage, setStage] = useState<"config" | "action" | "script" | "result">("config");
  const [warn, setWarn] = useState("");
  const [stuck, setStuck] = useState(false);
  const [noWork, setNoWork] = useState(false);
  const [info, setInfo] = useState(false);
  const [firstAction, setFirstAction] = useState<string | null>(null);
  const [actMsg, setActMsg] = useState("");
  const [ran, setRan] = useState(false);
  const [first, setFirst] = useState<DdCrit[] | null>(null);
  useGuide(onGuideChange, cfg.guide, stage);
  usePrefetch([c.maya]);
  const all = c.caps.every((k) => prof[k.id]);
  const optOf = (capId: string, optId?: string) => c.caps.find((k) => k.id === capId)?.options.find((o) => o.id === optId);

  const apply = (force = false) => {
    const crippled = prof.files === "none" || prof.commands === "none";
    if (crippled && !force) {
      setWarn("With this profile the agent cannot read the source or run the tests. You can change it, or apply it anyway and see what happens.");
      setStuck(true);
      audio.ui("error");
      return;
    }
    setWarn("");
    setStuck(false);
    const s = dd.get();
    dd.set({ scope: { ...prof }, scopeFirst: s.scopeFirst ?? { ...prof } });
    if (crippled) {
      setNoWork(true);
      audio.ui("error");
      commit("none");
      return;
    }
    audio.ui("success");
    setStage("action");
  };
  const act = (a: "run" | "inspect" | "reject") => {
    const fa = firstAction ?? a;
    if (!firstAction) setFirstAction(a);
    if (a === "inspect") return setStage("script");
    if (a === "reject") {
      setActMsg("Nothing ran, so the agent cannot check its own work. Reject what is unsafe, not everything.");
      audio.ui("error");
      return;
    }
    setRan(true);
    audio.ui("success");
    commit(fa);
  };
  const commit = (fa: string) => {
    const sf = dd.get().scopeFirst ?? prof;
    const name = (capId: string) => optOf(capId, sf[capId])?.label ?? "-";
    const actionName: Record<string, string> = { run: "Run these tests (without inspecting)", inspect: "Inspect script first", reject: "Reject all agent work", none: "None possible: the agent had no access to work with" };
    const cr = mkCrits(
      c.crits,
      {
        files: sf.files === "worktree",
        exec: sf.commands === "sandbox" && sf.database === "synthetic" && fa === "inspect",
        network: sf.network === "approved",
        connector: sf.connector === "issues",
        review: sf.changes === "review" && sf.deploy === "prepare",
      },
      {
        files: name("files"),
        exec: `Commands: ${name("commands")}; Database: ${name("database")}; first action: ${actionName[fa]}`,
        network: name("network"),
        connector: name("connector"),
        review: `Code changes: ${name("changes")}; Deployment: ${name("deploy")}`,
      },
    );
    const kept = dd.commitFirst("d2", cr, `Profile: ${c.caps.map((k) => optOf(k.id, sf[k.id])?.label).join(", ")}`);
    setFirst(kept);
    rec("dd_d2_first", `${dd.missionScore("d2")}/10`);
    dd.set({ d2Action: fa });
    setStage("result");
  };
  const scope = dd.get().scope ?? prof;
  const broad = c.caps.some((k) => scope[k.id] !== k.good);
  const allOk = !!first && first.every((x) => x.ok);
  const fo = focus ? optOf(focus.cap, focus.opt) : null;
  const fcap = focus ? c.caps.find((k) => k.id === focus.cap) : null;
  const settings = `{\n  "task": "ORB-1182 customer report export",\n${c.caps
    .filter((k) => prof[k.id])
    .map((k) => `  // ${k.label}\n  ${optOf(k.id, prof[k.id])!.rule.replace(/\n/g, "\n  ")}`)
    .join(",\n")}\n}`;

  return (
    <Win app={"OrbitAgent \u00b7 Settings"} tag={"S3 \u00b7 IDE"} clock={cfg.clock}>
      {stage === "config" && (
        <>
          <div className="dd-explainer">
            <b>Permissions for the AI coding agent</b>
            <span>{c.explainer}</span>
          </div>
          <div className="dd-settings">
            <section className="dd-caplist">
              {c.caps.map((k) => (
                <div key={k.id} className={`dd-caprow${focus?.cap === k.id ? " is-focus" : ""}`}>
                  <div className="dd-capq"><b>{k.label}</b><span>{k.question}</span></div>
                  <div className="dd-seg is-small" role="radiogroup" aria-label={k.label}>
                    {k.options.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        role="radio"
                        aria-checked={prof[k.id] === o.id}
                        className={prof[k.id] === o.id ? "is-on" : ""}
                        onMouseEnter={() => setFocus({ cap: k.id, opt: o.id })}
                        onFocus={() => setFocus({ cap: k.id, opt: o.id })}
                        onClick={() => {
                          audio.click();
                          setProf((p) => ({ ...p, [k.id]: o.id }));
                          setFocus({ cap: k.id, opt: o.id });
                        }}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </section>
            <aside className="dd-capdetail">
              {fo && fcap ? (
                <>
                  <span className="dd-kicker">{fcap.label} {"\u00b7"} what this option means</span>
                  <b className="dd-capdetail-t">{fo.label}</b>
                  <span className="dd-ih">Rule written to the agent settings</span>
                  <Code small>{fo.rule}</Code>
                  <span className="dd-ih">Example: the agent tries</span>
                  <Code small>{fo.example}</Code>
                  <p className="dd-capresult">{"\u2192"} {fo.result}</p>
                </>
              ) : (
                <p className="dd-muted">Point at or select an option to see the rule it writes and what the agent could then do.</p>
              )}
              <span className="dd-ih">settings.json (your profile so far)</span>
              <Code small>{settings}</Code>
            </aside>
          </div>
          <footer className="dd-foot">
            <div className="dd-alt">
              <button type="button" className="dd-link" onClick={() => setInfo((v) => !v)}>How this is enforced {info ? "\u25B4" : "\u25BE"}</button>
              {info && <small className="dd-muted dd-enf">{c.enforced}</small>}
              {warn && <span className="dd-warnline">{warn}</span>}
            </div>
            {stuck && <Btn onClick={() => apply(true)}>Apply anyway</Btn>}
            <Btn primary pulse={all} disabled={!all} onClick={() => apply()}>Apply profile</Btn>
          </footer>
        </>
      )}
      {(stage === "action" || stage === "script") && (
        <div className="dd-split">
          <aside className="dd-side">
            <MissionHead n={2} time="09:25" title="The agent's first action" sub="The profile is active. The agent asks before it runs this." />
            <div className={`dd-status${ran ? " is-run" : ""}`}>Development workspace {"\u00b7"} Action pending</div>
            <Bubble p={cfg.people.assistant} tone="ai">I'd like to run the export helper's unit tests to check the existing code before I change it.</Bubble>
          </aside>
          <section className="dd-main">
            <div className="dd-actcard">
              <span className="dd-kicker">Pending tool action (shown with your applied profile)</span>
              <dl className="dd-dl2">
                <div><dt>Action</dt><dd>Run reviewed export unit tests</dd></div>
                <div><dt>Environment</dt><dd>{scope.files === "worktree" ? "Isolated development worktree" : "Your entire home directory (includes ~/.config and ~/.ssh)"}</dd></div>
                <div><dt>Data</dt><dd>{scope.database === "synthetic" ? "Synthetic fixture only" : "Production database, read/write"}</dd></div>
                <div><dt>Production credentials</dt><dd>{scope.files === "home" || scope.database === "prod" ? "Reachable from this environment" : "Not present"}</dd></div>
                <div><dt>Outbound access</dt><dd>{scope.network === "approved" ? "Restricted by runtime policy" : "Unrestricted"}</dd></div>
                <div><dt>Commands</dt><dd>{scope.commands === "sandbox" ? "Isolated runtime" : "Your host shell"}</dd></div>
              </dl>
            </div>
            {stage === "script" && (
              <>
                <b className="dd-sub">What the script does</b>
                <Code small>{c.script}</Code>
              </>
            )}
            {actMsg && <span className="dd-warnline">{actMsg}</span>}
            <div className="dd-actions">
              <Btn onClick={() => act("reject")}>Reject all agent work</Btn>
              {stage === "action" && <Btn onClick={() => act("inspect")}>Inspect script first</Btn>}
              <Btn primary onClick={() => act("run")}>Run these tests</Btn>
            </div>
          </section>
        </div>
      )}
      {stage === "result" && first && (
        <MissionResult
          n={2}
          time="09:30"
          title="Agent profile applied"
          crits={first}
          outcomes={[
            noWork
              ? { tone: "warn", title: "The agent could not do the job", body: "With no access to the source, or no way to run the tests, it produced nothing and could not check its work. Too little access is not a safe choice; it just moves the work back to you." }
              : { tone: "ok", title: "12 tests passed \u00b7 synthetic fixture", body: "This covers the existing helper, not the security of the new endpoint." },
            ...(broad ? [{ tone: "info" as const, title: "Your profile stays applied as committed", body: "Its effect shows in the next task, and you reconcile the access there." }] : []),
          ]}
          expected={{
            exec: { Commands: "Reviewed scripts in an isolated runtime", Database: "Synthetic", "first action": "Inspect script first" },
          }}
          maya={{ person: cfg.people.maya, text: c.maya }}
          footer={<Btn primary pulse onClick={() => { if (allOk && !broad) dd.master("d2", false); finish("continue"); }}>Continue</Btn>}
        />
      )}
    </Win>
  );
}

/* ================================================================== D3 issue injection (S3) */
interface D3Content {
  provenance: string; issueTitle: string; source: string; effects: Record<string, string>; stepLegend: string;
  segments: { id: string; text: string; instr: boolean }[];
  steps: { id: string; text: string; legit: boolean; upload?: boolean; from: string }[];
  destination: string; maya: string; mayaMiss: string; whatIf: string;
  crits: { id: string; label: string }[];
  caps: Cap[];
}
const SEG_OPTS: DdOpt[] = [{ id: "info", label: "Bug information" }, { id: "instr", label: "Instruction" }];
const STEP_OPTS: DdOpt[] = [{ id: "keep", label: "Keep" }, { id: "remove", label: "Remove" }, { id: "pause", label: "Pause" }];

export function D3({ cfg, finish, onGuideChange, rec }: StageProps) {
  const c = cfg.content as D3Content;
  // Mission 2 may be outside the route: the team's default (safe) agent profile applies.
  const safeScope = Object.fromEntries(c.caps.map((k) => [k.id, k.good])) as Record<string, string>;
  const [marks, setMarks] = useState<Record<string, string>>({});
  const [steps, setSteps] = useState<Record<string, string>>({});
  const [inspected, setInspected] = useState(false);
  const [stage, setStage] = useState<"review" | "result" | "fix" | "fixed">("review");
  const [first, setFirst] = useState<DdCrit[] | null>(null);
  const [outcome, setOutcome] = useState<{ tone: "risk" | "warn" | "ok" | "info"; title: string; body: string }[]>([]);
  const [disclosed, setDisclosed] = useState(false);
  const [resp, setResp] = useState<Record<string, boolean>>({});
  const [showWhatIf, setShowWhatIf] = useState(false);
  const [explained, setExplained] = useState(false);
  const [scope, setScope] = useState<Record<string, string>>(() => ({ ...(dd.get().scope ?? safeScope) }));
  const [fixMsg, setFixMsg] = useState("");
  useGuide(onGuideChange, cfg.guide, stage === "fixed" ? "result" : stage);
  usePrefetch([c.maya, c.mayaMiss]);
  const ready = c.segments.every((s) => marks[s.id]) && c.steps.every((s) => steps[s.id]);
  const files = scope.files ?? "worktree";

  const submit = (kind: "submit" | "trust" | "besafe" | "discard") => {
    const eff: Record<string, string> =
      kind === "submit" ? steps : Object.fromEntries(c.steps.map((s) => [s.id, kind === "discard" ? "remove" : "keep"]));
    const mk = (id: string) => (marks[id] === "instr" ? "Instruction" : "Bug information");
    const kindName: Record<string, string> = { submit: "Submitted your reviewed plan", trust: "Trusted the issue and ran all steps", besafe: "Added \u201cbe safe\u201d and ran all steps", discard: "Discarded the whole issue" };
    const cr = mkCrits(
      c.crits,
      {
        s3: marks.s3 === "instr",
        s4: marks.s4 === "instr",
        reject: eff.p2 !== "keep" && eff.p3 !== "keep",
        inspect: inspected && eff.p3 !== "keep",
        valid: kind === "submit" && eff.p1 === "keep" && eff.p4 === "keep",
      },
      {
        s3: `Sentence 3 marked: ${mk("s3")}`,
        s4: `Sentence 4 marked: ${mk("s4")}`,
        reject: `Step 2: ${eff.p2}; step 3: ${eff.p3}`,
        inspect: `${inspected ? "Inspected the upload" : "Did not inspect the upload"}; step 3: ${eff.p3}`,
        valid: `${kindName[kind]}; step 1: ${eff.p1}; step 4: ${eff.p4}`,
      },
    );
    const sc = dd.get().scope ?? safeScope;
    const kept = dd.commitFirst("d3", cr, kind === "submit" ? `Submitted plan: ${c.steps.map((s) => `${s.id.slice(1)} ${eff[s.id]}`).join(", ")}` : kind === "trust" ? "Trusted the issue and ran every step" : kind === "besafe" ? "Added \u201cbe safe\u201d to the prompt and ran every step" : "Discarded the whole issue");
    setFirst(kept);
    rec("dd_d3_first", `${dd.missionScore("d3")}/10`);
    const out: { tone: "risk" | "warn" | "ok" | "info"; title: string; body: string }[] = [];
    if (eff.p3 === "keep") {
      if (sc.network === "approved") {
        out.push({ tone: "warn", title: "Policy denial: upload blocked", body: `The runtime blocked the outbound request to ${c.destination}: the destination is not on the allow list. No upload occurred.` });
        dd.log("d3", "The agent tried to upload diagnostics; runtime policy blocked it.", "blocked");
      } else if (sc.files === "home") {
        out.push({ tone: "risk", title: "Simulated disclosure: credential file uploaded", body: `The agent read ~/.config/astercloud/staging.env (a mock credential file outside the worktree) and sent it to ${c.destination}. Receipt DX-2291. No real secret exists in this simulation.` });
        dd.log("d3", "The agent uploaded a mock credential file to an outside collector.", "consequence");
        rec("dd_d3_upload", "credential file");
        setDisclosed(true);
      } else {
        out.push({ tone: "warn", title: "Unauthorized upload of synthetic diagnostics", body: `The agent sent synthetic diagnostics from the worktree to ${c.destination}. No credentials were reachable from the worktree, so none were sent.` });
        dd.log("d3", "The agent uploaded synthetic diagnostics to an outside collector.", "consequence");
        rec("dd_d3_upload", "synthetic diagnostics");
      }
    }
    if (eff.p2 === "keep") out.push({ tone: "warn", title: "Tenant-isolation test disabled for this run", body: "The reproduction ran without the security test. You will restore it in the repair." });
    const paused = c.steps.filter((s) => eff[s.id] === "pause");
    if (paused.length) out.push({ tone: "info", title: "Paused steps are waiting", body: `Step ${paused.map((s) => s.id.slice(1)).join(" and ")} did not run and stays pending until you decide.` });
    if (kind === "discard") out.push({ tone: "warn", title: "The real bug is still open", body: "Discarding the issue also dropped the useful reproduction steps." });
    if (kind === "besafe") out.push({ tone: "info", title: "A prompt warning is not a control", body: "Adding \u201cbe safe\u201d did not remove the unauthorized steps from the plan." });
    if (eff.p3 !== "keep" && eff.p2 !== "keep" && kind === "submit") out.push({ tone: "ok", title: "Plan executed: reproduction and layout fix only", body: "No upload was queued, so nothing needed to be stopped at run time." });
    setOutcome(out);
    audio.ui(out.some((o) => o.tone === "risk") ? "error" : "message");
    setStage("result");
  };

  const allOk = !!first && first.every((x) => x.ok);
  const scopeBad = c.caps.some((k) => (dd.get().scope ?? safeScope)[k.id] !== k.good);
  const respNeeded = ["stop", "notify", "revoke"];
  const respOk = !disclosed || respNeeded.every((k) => resp[k]);
  const segGood = (s: { id: string; instr: boolean }) => (marks[s.id] === "instr") === s.instr;
  const stepGood = (s: { id: string; legit: boolean }) => (s.legit ? steps[s.id] === "keep" : steps[s.id] === "remove");

  const applyFix = () => {
    const okAll = c.segments.every(segGood) && c.steps.every(stepGood) && c.caps.every((k) => scope[k.id] === k.good);
    if (!okAll) {
      setFixMsg("Some items still need a fix.");
      audio.ui("error");
      return;
    }
    dd.set({ scope: { ...scope } });
    dd.log("d3", "Repaired the plan and reduced the agent's access to what the task needs.", "corrected");
    audio.ui("success");
    setStage("fixed");
  };

  const segBlock = (fix: boolean) => (
    <div className="dd-issue">
      <div className="dd-issue-h">
        <b>{c.issueTitle}</b>
        <span className="dd-prov">{c.provenance}</span>
        <small className="dd-muted">{c.source}</small>
      </div>
      {c.segments.map((s, i) => (
        <div key={s.id} className={`dd-segline${fix && !segGood(s) ? " is-fix" : !marks[s.id] ? " is-todo" : ""}`}>
          <p><span className="dd-segn">{i + 1}</span>{s.text}</p>
          <Seg small options={SEG_OPTS} value={marks[s.id]} onChange={(v) => setMarks((m) => ({ ...m, [s.id]: v }))} />
          {marks[s.id] ? <small className={`dd-effect is-${marks[s.id]}`}>{c.effects[marks[s.id]]}</small> : <small className="dd-effect"><Todo label="Mark this line" /></small>}
          {fix && !segGood(s) && <FixTag />}
        </div>
      ))}
    </div>
  );
  const planBlock = (fix: boolean) => (
    <div className="dd-plan">
      <div className="dd-plan-h"><b>The agent's plan for issue #482</b><span className="dd-muted">{c.stepLegend}</span></div>
      {c.steps.map((s, i) => (
        <div key={s.id} className={`dd-step${fix && !stepGood(s) ? " is-fix" : !steps[s.id] ? " is-todo" : ""}`}>
          <span className="dd-step-n">{i + 1}</span>
          <div className="dd-step-t">
            <p>{s.text} <span className="dd-from">from {s.from}</span>{!steps[s.id] && !fix && <Todo />}</p>
            {s.upload && (
              <button type="button" className="dd-link" onClick={() => { setInspected(true); audio.click(); }}>Inspect proposed upload</button>
            )}
            {s.upload && inspected && (
              <Code small>{`tool: http.upload\nsource: ./diagnostics/report-482.json${files === "home" ? "\n        ~/.config/astercloud/staging.env  (credential file)" : "\n        (worktree only: synthetic diagnostics)"}\ndestination: ${c.destination}\n(inspecting never opens a connection)`}</Code>
            )}
            {fix && !stepGood(s) && <FixTag />}
          </div>
          <Seg small options={fix ? STEP_OPTS.filter((o) => o.id !== "pause") : STEP_OPTS} value={steps[s.id]} onChange={(v) => setSteps((m) => ({ ...m, [s.id]: v }))} />
        </div>
      ))}
    </div>
  );

  return (
    <Win app={"OrbitIDE \u00b7 Coding assistant"} tag={"S3 \u00b7 IDE"} clock={cfg.clock}>
      {stage === "review" && (
        <>
          <div className="dd-two">
            <section>{segBlock(false)}</section>
            <section>{planBlock(false)}</section>
          </div>
          <footer className="dd-foot">
            <div className="dd-alt">
              <span className="dd-muted">Or:</span>
              <Btn small disabled={!ready} onClick={() => submit("trust")}>Trust the issue and run all</Btn>
              <Btn small disabled={!ready} onClick={() => submit("besafe")}>Add {"\u201c"}be safe{"\u201d"} to the prompt and continue</Btn>
              <Btn small disabled={!ready} onClick={() => submit("discard")}>Discard the whole issue</Btn>
            </div>
            <Btn primary pulse={ready} disabled={!ready} onClick={() => submit("submit")}>Submit reviewed plan</Btn>
          </footer>
        </>
      )}
      {(stage === "result" || stage === "fixed") && first && (
        <MissionResult
          n={3}
          time={stage === "fixed" ? "10:05" : "09:50"}
          title={stage === "fixed" ? "Plan and access repaired" : "What the agent actually did"}
          crits={first}
          outcomes={stage === "fixed" ? [{ tone: "ok", title: "Reproduction and layout fix running with enforced scope", body: "Corrected during training. Any earlier upload stays in your history." }] : outcome}
          gate={
            stage === "result" && disclosed ? (
              <div className="dd-checks is-inline">
                {[["stop", "Stop the agent's session"], ["notify", "Notify security and the credential owner"], ["revoke", "Ask the owner to revoke and rotate the staging credential"]].map(([id, label]) => (
                  <button key={id} type="button" role="checkbox" aria-checked={!!resp[id]} className={`dd-check${resp[id] ? " is-on" : ""}`} onClick={() => { audio.click(); setResp((r) => ({ ...r, [id]: !r[id] })); }}>
                    <span className="dd-box" aria-hidden="true">{resp[id] ? "\u2713" : ""}</span>{label}
                  </button>
                ))}
              </div>
            ) : undefined
          }
          more={
            stage === "result" && allOk && !scopeBad ? (
              <>
                <button type="button" className="dd-link" onClick={() => setShowWhatIf((v) => !v)}>What would have happened with broader access?</button>
                {showWhatIf && <WhatIf>{c.whatIf}</WhatIf>}
              </>
            ) : undefined
          }
          maya={{ person: cfg.people.maya, text: allOk ? c.maya : c.mayaMiss, speak: stage === "result" }}
          footer={
            stage === "fixed" ? (
              <Btn primary pulse onClick={() => { dd.master("d3", true); if (dd.first("d2")) dd.master("d2", !(dd.first("d2")!.crits.every((x) => x.ok))); finish("continue"); }}>Continue</Btn>
            ) : !respOk ? (
              <span className="dd-warnline">Respond to the disclosure first: tick all three actions above.</span>
            ) : allOk && !scopeBad ? (
              <Btn primary pulse onClick={() => { dd.master("d3", false); if (dd.first("d2")) dd.master("d2", !(dd.first("d2")!.crits.every((x) => x.ok))); finish("continue"); }}>Continue</Btn>
            ) : (
              <MissFooter
                allOk={false}
                explained={explained}
                onExplain={() => { setExplained(true); audio.ui("docOpen"); }}
                onRetry={() => { setFixMsg(""); setStage("fix"); }}
                onContinue={() => { if (allOk) dd.master("d3", false); else dd.acknowledge("d3"); if (dd.first("d2")) dd.master("d2", !(dd.first("d2")!.crits.every((x) => x.ok))); finish("continue"); }}
                retryLabel={allOk ? "Reconcile the agent's access" : "Try again to improve your score"}
              />
            )
          }
        />
      )}
      {stage === "fix" && (
        <>
          <div className="dd-two">
            <section>
              {segBlock(true)}
            </section>
            <section>
              {planBlock(true)}
              <div className="dd-scopefix">
                <b>Agent access</b>
                {c.caps.map((k) => (
                  <div key={k.id} className={`dd-cap is-row${scope[k.id] !== k.good ? " is-fix" : ""}`}>
                    <span>{k.label}{scope[k.id] !== k.good && <FixTag />}</span>
                    <Seg small options={k.options.filter((o) => o.id !== "none")} value={scope[k.id]} onChange={(v) => setScope((p) => ({ ...p, [k.id]: v }))} />
                  </div>
                ))}
              </div>
            </section>
          </div>
          <footer className="dd-foot">
            {fixMsg ? <span className="dd-warnline">{fixMsg}</span> : <span className="dd-muted">Corrections update your mastery, not your first-attempt score.</span>}
            <KeepInstead onClick={() => { setExplained(true); setFixMsg(""); setStage("result"); }} />
            <Btn primary pulse onClick={applyFix}>Apply fixes</Btn>
          </footer>
        </>
      )}
    </Win>
  );
}

export type { DdCrit };
